import * as THREE from 'three';
import { basic, emis, std, stone } from '../materials.ts';
import { M, Ribbon, V, glowSprite } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Sierpe del caparazón (era IV, docs/CRIATURAS.md «Bastión de la Vigilia»): a serpent of the titan's own rock
 * that lives between the plates of the shell. It travels under the ground (`shellSerpentBurrow`: only a ridge of
 * heaved earth with a row of plate tips cutting through it like fins, that cannot be struck) and bursts up
 * inside the walls, rearing out of a crater of broken earth and shell (`shellSerpent`): a body of segments each
 * armoured with hex scutes like the Tortoise's shell, moss in the seams, a pale ribbed belly, a long wedge of a
 * head with two horns raking back and a hood of plates flared behind it, its jaws open on acid (#c6e21a) that
 * glows in its throat and drips off its fangs.
 *
 * Emerged, bones: `crater` (the broken ground it came out of), seg1…seg6 up the body from the ground to the neck
 * (each the parent of the next), head and jaw. Faces +Z: the body rises behind the crater's centre and arches
 * forward over it. The burrow is a model of its own: `ridge` (the heave), `plates` (the fins) and `dust`.
 */

export const SERPENT_SEGS = 6;
export const SHELL_SERPENT_HEIGHT = 2.35;

const ACID = '#c6e21a';

/** The serpent's materials (shared by both looks). */
const mats = (): Record<string, THREE.Material> => ({
  plate: stone('#5d6163', { bumpScale: 0.04 }),
  plateD: stone('#33383b'),
  plateL: stone('#8a8f90'),
  belly: std('#3f4540', { roughness: 0.85, flatShading: true, bumpScale: 0.03 }),
  flesh: std('#171a19', { roughness: 0.9 }),
  moss: std('#3f4d2b', { roughness: 1, flatShading: true, bumpScale: 0.05 }),
  mossL: std('#6c7c3e', { roughness: 1, flatShading: true, bumpScale: 0.05 }),
  earth: stone('#2b2a29', { bumpScale: 0.06 }),
  earthL: stone('#3e3d3b', { bumpScale: 0.06 }),
  fang: std('#d3dadc', { roughness: 0.5 }),
  acid: emis('#efff8a', ACID, 2.0),
  dust: basic('#4b4a48', 0.3),
});

const UP = V(0, 1, 0);

/** A hex scute lying on a surface with normal `n`. */
const scute = (parent: THREE.Object3D, mat: THREE.Material, at: THREE.Vector3, n: THREE.Vector3, r: number, h = 0.06): THREE.Mesh => {
  const m = M(new THREE.CylinderGeometry(r * 0.8, r, h, 6), mat, parent);
  m.position.copy(at);
  m.quaternion.setFromUnitVectors(UP, n.clone().normalize());
  return m;
};

/** Broken ground: a ring of heaved earth and tilted slabs of shell round (0, 0, cz). */
const crater = (parent: THREE.Object3D, m: Record<string, THREE.Material>, k4: ReturnType<typeof kit>, rng: () => number, cz: number, r: number): void => {
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2 + rng() * 0.3;
    const rr = r + rng() * 0.08;
    k4.lump(parent, i % 2 ? m.earth! : m.earthL!, Math.sin(a) * rr, 0.06, cz + Math.cos(a) * rr * 0.9, 0.16 + rng() * 0.06, 0.1 + rng() * 0.06, 0.15, 0.5, 0);
  }
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + 0.5;
    const rr = r + 0.06;
    const slab = M(new THREE.CylinderGeometry(0.17, 0.2, 0.05, 6), i % 2 ? m.plate! : m.plateL!, parent);
    slab.position.set(Math.sin(a) * rr, 0.12 + (i % 2) * 0.04, cz + Math.cos(a) * rr);
    slab.rotation.set(Math.cos(a) * 1.0, rng(), -Math.sin(a) * 1.0);
  }
  k4.swell(parent, m.flesh!, 0, 0.01, cz, r * 0.92, 0.02, r * 0.85, 8, 3);
  for (let i = 0; i < 5; i++) {
    const a = rng() * 6.3;
    k4.lump(parent, m.earthL!, Math.sin(a) * r * 1.6, 0.03, cz + Math.cos(a) * r * 1.5, 0.06, 0.04, 0.06, 0.5, 0);
  }
};

export const shellSerpent = (o?: BaseOpts): Model => {
  const c = begin(o, 0.8, 613);
  const { g, rng } = c;
  const k4 = kit(rng);
  const m = mats();

  const craterB = joint(g, V(0, 0, -0.25), 'crater');
  crater(craterB.abs, m, k4, rng, -0.25, 0.46);

  /* ---------- the body: hex-armoured segments rising in an S from the crater and arching forward ---------- */
  const spine = new THREE.CatmullRomCurve3([
    V(0, -0.2, -0.3), V(0.04, 0.42, -0.4), V(0.1, 0.95, -0.32), V(0.02, 1.45, -0.12), V(-0.06, 1.85, 0.12), V(-0.02, 2.08, 0.4), V(0, 2.12, 0.64),
  ]);
  const segs: THREE.Object3D[] = [];
  let parent: THREE.Object3D = g;
  for (let i = 0; i < SERPENT_SEGS; i++) {
    const u0 = i / SERPENT_SEGS, u1 = (i + 1) / SERPENT_SEGS;
    const a = spine.getPoint(u0), b = spine.getPoint(u1);
    const B = joint(parent, a, `seg${i + 1}`);
    const s = B.abs;
    const dir = b.clone().sub(a);
    const len = dir.length();
    dir.normalize();
    const r = 0.25 - i * 0.02;
    const mid = a.clone().lerp(b, 0.5);
    const q = new THREE.Quaternion().setFromUnitVectors(UP, dir);
    // The flesh core (overlapping its neighbours so the bends stay closed) and the dark collar at each joint.
    const core = M(new THREE.CylinderGeometry(r * 0.9, r * 0.96, len * 1.2, 8), m.flesh!, s);
    core.position.copy(mid);
    core.quaternion.copy(q);
    // `out` is the back of the curve (it faces away from +Z); `side` across it.
    const side = new THREE.Vector3().crossVectors(dir, V(0, 0, 1)).normalize();
    const out = new THREE.Vector3().crossVectors(dir, side).normalize();
    if (out.z > 0) out.negate();
    // The back: a ridge of big scutes, smaller ones down each flank, moss in the seams.
    for (const f of [0.28, 0.75]) {
      const at = a.clone().lerp(b, f);
      scute(s, m.plateL!, at.clone().addScaledVector(out, r * 0.86), out, r * 0.6, 0.08);
      for (const k of [-1, 1]) scute(s, m.plate!, at.clone().addScaledVector(out, r * 0.45).addScaledVector(side, k * r * 0.78), out.clone().multiplyScalar(0.6).addScaledVector(side, k), r * 0.45, 0.06);
    }
    if (i % 2 === 1) k4.lump(s, i % 4 === 1 ? m.moss! : m.mossL!, mid.x + side.x * r * 0.5, mid.y, mid.z + out.z * r * 0.95, r * 0.3, 0.04, r * 0.26, 0.4, 0);
    // The belly (toward +Z): pale ribbed bands.
    for (const f of [0.25, 0.75]) {
      const band = M(new THREE.BoxGeometry(r * 1.2, len * 0.42, 0.07), m.belly!, s);
      band.position.copy(a.clone().lerp(b, f)).addScaledVector(out, -r * 0.8);
      band.quaternion.copy(q);
    }
    segs.push(B.p);
    parent = s;
  }

  /* ---------- the head: a long wedge, horns raking back, a hood of plates, the jaws open on acid ---------- */
  const neck = spine.getPoint(1);
  const headB = joint(parent, neck, 'head');
  const head = headB.abs;
  const hy = neck.y, hz = neck.z;
  {
    // The skull: a box pinched to a snout, tipped a little nose down.
    const geo = new THREE.BoxGeometry(0.32, 0.15, 0.5, 2, 1, 3);
    const pos = geo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      const z = pos.getZ(i);
      const f = (z + 0.25) / 0.5;
      pos.setX(i, pos.getX(i) * (1 - 0.45 * f));
      pos.setY(i, pos.getY(i) * (1 - 0.3 * f) + (pos.getY(i) > 0 ? 0 : 0.02 * f));
    }
    geo.computeVertexNormals();
    const sk = M(geo, m.plate!, head);
    sk.position.set(0, hy + 0.02, hz + 0.24);
    sk.rotation.x = 0.12;
    scute(head, m.plateL!, V(0, hy + 0.1, hz + 0.16), V(0, 1, 0.15), 0.11, 0.05);
    scute(head, m.plate!, V(0, hy + 0.08, hz + 0.34), V(0, 1, 0.3), 0.07, 0.04);
  }
  for (const k of [-1, 1]) {
    // Brow ridge, eye, and a horn sweeping back off it.
    const br = M(new THREE.BoxGeometry(0.07, 0.04, 0.16), m.plateD!, head);
    br.position.set(k * 0.12, hy + 0.09, hz + 0.26);
    br.rotation.set(0.15, -k * 0.3, k * 0.3);
    M(new THREE.SphereGeometry(0.024, 5, 3), m.acid!, head).position.set(k * 0.13, hy + 0.05, hz + 0.3);
    glowSprite(head, ACID, 0.22, V(k * 0.14, hy + 0.05, hz + 0.32), 0.85);
    k4.shard(head, m.plateL!, V(k * 0.12, hy + 0.09, hz + 0.14), V(k * 0.35, 0.35, -1), 0.34, 0.035);
  }
  // The hood: a fan of plates flared behind the head.
  for (let i = 0; i < 5; i++) {
    const a = (i - 2) * 0.5;
    const at = V(Math.sin(a) * 0.24, hy + 0.04 - Math.abs(a) * 0.12, hz - 0.08 - Math.cos(a) * 0.03);
    scute(head, i % 2 ? m.plate! : m.plateL!, at, V(Math.sin(a) * 0.4, 0.3, -1), 0.13 - Math.abs(i - 2) * 0.012, 0.04);
  }
  // Upper fangs, curved down, and the acid glowing in the throat.
  for (const k of [-1, 1]) {
    const f = M(new THREE.ConeGeometry(0.026, 0.16, 4), m.fang!, head);
    f.position.set(k * 0.07, hy - 0.11, hz + 0.43);
    f.rotation.set(Math.PI - 0.2, 0, 0);
  }
  k4.swell(head, m.acid!, 0, hy - 0.06, hz + 0.26, 0.1, 0.035, 0.16, 6, 3);
  glowSprite(head, ACID, 0.95, V(0, hy - 0.1, hz + 0.45), 0.7);
  // The lower jaw, dropped open in its rest pose, fangs up, acid hanging off it.
  const jawB = joint(head, V(0, hy - 0.05, hz + 0.08), 'jaw');
  const jaw = jawB.abs;
  {
    const jg = new THREE.Group();
    jg.position.set(0, hy - 0.05, hz + 0.08);
    jg.rotation.x = 0.42;
    jaw.add(jg);
    const geo = new THREE.BoxGeometry(0.26, 0.06, 0.42, 2, 1, 2);
    const pos = geo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) pos.setX(i, pos.getX(i) * (1 - 0.45 * ((pos.getZ(i) + 0.21) / 0.42)));
    geo.computeVertexNormals();
    M(geo, m.plateD!, jg).position.set(0, -0.04, 0.2);
    for (const k of [-1, 1]) {
      const f = M(new THREE.ConeGeometry(0.018, 0.08, 4), m.fang!, jg);
      f.position.set(k * 0.06, 0.02, 0.36);
    }
    for (const [x, z, l] of [[0.03, 0.38, 0.12], [-0.05, 0.3, 0.08], [0.0, 0.2, 0.06]] as const) {
      const d = M(new THREE.ConeGeometry(0.013, l, 4), m.acid!, jg);
      d.position.set(x, -0.07 - l / 2, z);
      d.rotation.x = Math.PI - 0.42;
    }
    k4.swell(jg, m.acid!, 0, -0.005, 0.22, 0.07, 0.02, 0.12, 6, 3);
  }

  const bones: Record<string, THREE.Object3D> = { crater: craterB.p };
  segs.forEach((s, i) => (bones[`seg${i + 1}`] = s));
  bones.head = headB.p;
  bones.jaw = jawB.p;
  return done(c, SHELL_SERPENT_HEIGHT, () => undefined, undefined, bonesOf(c, bones));
};

/**
 * Underground: a ridge of earth heaved up ahead of it as it tunnels, a row of plate tips cutting through the crest
 * like fins, a crack seeping a sickly glow at the head, clods tumbling off and dust smoking. Low and long (faces +Z).
 */
export const shellSerpentBurrow = (o?: BaseOpts): Model => {
  const c = begin(o, 0.75, 617);
  const { g, rng } = c;
  const k4 = kit(rng);
  const m = mats();

  const ridgeB = joint(g, V(0, 0, 0), 'ridge');
  const rd = ridgeB.abs;
  // The heave: high and broken at the head (front), trailing off behind into a furrow.
  for (let i = 0; i < 9; i++) {
    const z = 0.5 - i * 0.15;
    const h = Math.max(0.06, 0.24 - i * 0.022);
    const w = 0.3 - i * 0.018;
    for (const k of [-1, 1]) {
      k4.lump(rd, (i + (k > 0 ? 1 : 0)) % 2 ? m.earth! : m.earthL!, k * w * 0.42, h * 0.4, z + k * 0.03, w * 0.5, h * 0.7, 0.12, 0.6, 0);
    }
  }
  // The bow wave: slabs of the shell's skin turned up either side of the head.
  for (const k of [-1, 1]) {
    for (let i = 0; i < 2; i++) {
      const slab = M(new THREE.CylinderGeometry(0.12, 0.14, 0.045, 6), i ? m.plateD! : m.plate!, rd);
      slab.position.set(k * (0.28 + i * 0.06), 0.1, 0.5 - i * 0.18);
      slab.rotation.set(0.3, 0, -k * (1.0 + i * 0.2));
    }
  }
  // A crack along the head of the heave, the acid's glow seeping up through it.
  k4.crack(rd, [V(-0.08, 0.27, 0.62), V(0, 0.29, 0.5), V(0.04, 0.26, 0.36)], 0.014, m.acid!, 0.03);
  glowSprite(rd, ACID, 0.5, V(0, 0.3, 0.52), 0.4);
  // Clods tumbling off either side.
  for (let i = 0; i < 8; i++) {
    const k = i % 2 ? 1 : -1;
    k4.lump(rd, m.earth!, k * (0.3 + rng() * 0.14), 0.03, 0.45 - i * 0.14, 0.05, 0.035, 0.05, 0.5, 0);
  }
  // The fins: the tips of its back plates in a row along the crest, raking back.
  const platesB = joint(rd, V(0, 0.2, 0.0), 'plates');
  const pl = platesB.abs;
  for (const [z, s, y] of [[0.3, 0.12, 0.26], [0.08, 0.15, 0.24], [-0.16, 0.13, 0.2], [-0.4, 0.1, 0.14]] as const) {
    const f = M(new THREE.CylinderGeometry(s * 0.8, s, 0.05, 6), m.plateL!, pl);
    f.position.set(0, y + s * 0.4, z);
    f.rotation.set(0.5, 0, Math.PI / 2);
  }
  k4.lump(pl, m.mossL!, 0.03, 0.27, 0.1, 0.06, 0.03, 0.05, 0.3, 0);

  const dustB = joint(g, V(0, 0, 0.5), 'dust');
  const dust: Ribbon[] = [];
  for (const [x, z, h] of [[0.26, 0.5, 0.42], [-0.24, 0.42, 0.38]] as const) {
    dust.push(new Ribbon(dustB.abs, [V(x, 0.12, z), V(x * 1.3, h * 0.5, z - 0.1), V(x * 1.2, h, z - 0.25)], 0.07, m.dust!, rng, 0.06));
  }
  const t = (): void => {
    for (const d of dust) d.step();
  };
  t();
  return done(c, 0.45, t, undefined, bonesOf(c, { ridge: ridgeB.p, plates: platesB.p, dust: dustB.p }));
};
